import React, { useEffect, useCallback } from 'react';
import { usePomodoro } from '../hooks/usePomodoro';
import { usePomodoroSettings, useUpdatePomodoroSettings } from '../api/pomodoroSettingsApi';
import { usePomodoroStats, useAddCompletedPomodoro } from '../api/pomodoroStatsApi';
import { minutesToSeconds } from '../utils/timeFormatters';
import PomodoroDisplay from '../components/PomodoroDisplay';
import SessionIndicator from '../components/SessionIndicator';
import TimerControls from '../components/TimerControls';
import SettingsPanel from '../components/SettingsPanel';

const PomodoroPage: React.FC = () => {
  const { data: settings, isLoading: settingsLoading } = usePomodoroSettings();
  const { data: pomodoroStats, isLoading: statsLoading } = usePomodoroStats();
  const updateSettingsMutation = useUpdatePomodoroSettings();
  const addCompletedPomodoroMutation = useAddCompletedPomodoro();
  
  // Provide default settings while loading
  const currentSettings = settings || {
    workDuration: 25,
    shortBreakDuration: 5,
    longBreakDuration: 15,
    autoStart: false,
    soundEnabled: true
  };
  
  // Callback for when a Pomodoro work session is completed - Fixed with useCallback
  const handlePomodoroCompleted = useCallback(async () => {
    await addCompletedPomodoroMutation.mutateAsync();
  }, [addCompletedPomodoroMutation]);
  
  const { timerState, toggleTimer, resetTimer, nextSession, skipSession } = usePomodoro(
    currentSettings, 
    handlePomodoroCompleted
  );

  // Calculate total time for current session
  const getTotalTimeForSession = () => {
    switch (timerState.sessionType) {
      case 'WORK':
        return minutesToSeconds(currentSettings.workDuration);
      case 'SHORT_BREAK':
        return minutesToSeconds(currentSettings.shortBreakDuration);
      case 'LONG_BREAK':
        return minutesToSeconds(currentSettings.longBreakDuration);
      default:
        return minutesToSeconds(currentSettings.workDuration);
    }
  };

  // Keyboard shortcuts - Fixed to not interfere with browser shortcuts or navigation
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Prevent shortcuts when typing in input fields
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Only handle plain key presses (no modifiers) to avoid interfering with browser shortcuts
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }

      // Only handle keyboard shortcuts if the page is visible and active
      if (document.hidden) {
        return;
      }

      switch (event.code) {
        case 'Space':
          event.preventDefault();
          toggleTimer();
          break;
        case 'KeyR':
          event.preventDefault();
          resetTimer();
          break;
        case 'KeyN':
          if (timerState.isCompleted) {
            event.preventDefault();
            nextSession();
          }
          break;
        case 'KeyS':
          if (!timerState.isCompleted) {
            event.preventDefault();
            skipSession();
          }
          break;
      }
    };

    // Only add listener when component is mounted and settings are loaded
    if (!settingsLoading) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [toggleTimer, resetTimer, nextSession, skipSession, timerState.isCompleted, settingsLoading]);

  // Show loading state while settings are loading
  if (settingsLoading || statsLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text">Loading timer...</p>
        </div>
      </div>
    );
  }

  // Get today's stats for display
  const todayStats = pomodoroStats?.dailyStats.find(
    day => day.date === new Date().toISOString().split('T')[0]
  ) || { date: new Date().toISOString().split('T')[0], completedPomodoros: 0, fullCycles: 0 };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-text mb-2">
          Pomodoro Timer
        </h1>
        <p className="text-text/70 max-w-2xl mx-auto">
          Stay focused and productive with the Pomodoro Technique. 
          Work for 25 minutes, then take a 5-minute break.
        </p>
      </div>

      <div className="bg-white rounded-2xl p-8 shadow-sm">
        <div className="max-w-2xl mx-auto">
          {/* Session indicator */}
          <div className="mb-8">
            <SessionIndicator
              sessionType={timerState.sessionType}
              pomodoroCount={timerState.pomodoroCount}
              isCompleted={timerState.isCompleted}
            />
          </div>

          {/* Timer display */}
          <div className="mb-8">
            <PomodoroDisplay
              timeLeft={timerState.timeLeft}
              totalTime={getTotalTimeForSession()}
              sessionType={timerState.sessionType}
              isRunning={timerState.isRunning}
            />
          </div>

          {/* Controls */}
          <div className="mb-8">
            <TimerControls
              isRunning={timerState.isRunning}
              isCompleted={timerState.isCompleted}
              onToggle={toggleTimer}
              onReset={resetTimer}
              onNext={timerState.isCompleted ? nextSession : undefined}
              onSkip={!timerState.isCompleted ? skipSession : undefined}
            />
          </div>

          {/* Statistics - Now using real stored data */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-semibold text-text mb-4 text-center">
              Today's Progress
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-center p-4 bg-accent/20 rounded-lg">
                <div className="text-2xl font-bold text-primary">
                  {todayStats.completedPomodoros}
                </div>
                <div className="text-sm text-primary">
                  Completed Pomodoros
                </div>
              </div>
              <div className="text-center p-4 bg-accent/10 rounded-lg">
                <div className="text-2xl font-bold text-text">
                  {todayStats.fullCycles}
                </div>
                <div className="text-sm text-text">
                  Full Cycles
                </div>
              </div>
            </div>

            {/* Additional stats from stored data */}
            {pomodoroStats && (
              <div className="mt-4 pt-4 border-t border-accent">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-primary/10 rounded-lg">
                    <div className="text-xl font-bold text-primary">
                      {pomodoroStats.currentStreak}
                    </div>
                    <div className="text-sm text-primary">
                      Current Streak (days)
                    </div>
                  </div>
                  <div className="text-center p-3 bg-accent/20 rounded-lg">
                    <div className="text-xl font-bold text-text">
                      {pomodoroStats.totalPomodoros}
                    </div>
                    <div className="text-sm text-text">
                      Total Pomodoros
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Full Settings Panel */}
          <SettingsPanel 
            settings={currentSettings}
            onUpdateSettings={(updates) => updateSettingsMutation.mutate(updates)}
          />

          {/* Tips */}
          <div className="mt-8 text-center text-sm text-text/60">
            <p className="mb-2">
              💡 <strong>Pro tip:</strong> During breaks, step away from your screen and stretch!
            </p>
            {currentSettings.autoStart && (
              <p className="text-primary font-medium">
                🔄 Auto-start is enabled - sessions will transition automatically
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PomodoroPage; 